// Which hiring-assistant flow the recruiter is currently in.
//
// The flow is picked on the welcome screen and persisted client-side — the backend
// infers flow_1/flow_2 from which id is supplied (assistant_id vs assessment_id),
// but the talent audit runs on the same assessment_id as flow_2 and so must be
// disambiguated with an explicit `flow_type` on every request.
export type AssistantFlow = 'flow_1' | 'flow_2' | 'flow_talent_audit';

const STORAGE_KEY = 'hiringAssistantFlowType';

/** The `flow_type` value the API expects for talent audit requests. */
export const TALENT_AUDIT_FLOW_TYPE = 'talent_audit';

/** The welcome-screen option value that starts the talent audit flow. */
export const TALENT_AUDIT_OPTION = 'talent_audit';

/**
 * The audit choices shown after the recruiter enters Flow 3.
 *
 * The backend does not persist the client-side Flow 3 entry point. On some
 * history responses it also reports `flow_type: "hiring"`, so the first user
 * answer is the only durable discriminator. Keep these wire values centralized
 * so every audit branch can be recognized on reload.
 */
export const TALENT_AUDIT_TYPE_VALUES = [
  'role_fit_performance',
  'skills_capability_audit',
  'promotion_readiness',
  'leadership_potential',
  'team_dynamics',
] as const;

const TALENT_AUDIT_TYPE_VALUE_SET = new Set<string>(TALENT_AUDIT_TYPE_VALUES);

export const getAssistantFlow = (): AssistantFlow | null =>
  (localStorage.getItem(STORAGE_KEY) as AssistantFlow | null) ?? null;

export const setAssistantFlow = (flow: AssistantFlow) => {
  localStorage.setItem(STORAGE_KEY, flow);
};

export const isTalentAudit = () => getAssistantFlow() === 'flow_talent_audit';

/** The numeric `flow` the conversation-sessions list reports for a talent audit. */
export const TALENT_AUDIT_FLOW_NUMBER = 3;

// States that only ever occur in the talent audit state machine. flow_1 and
// flow_2 use get_jd_choice / create_jd / submit_jd / upload_jd / get_role /
// criteria / technical_competence / set_timeline / done, so there is no overlap.
export const TALENT_AUDIT_STATES = new Set([
  'get_audit_type',
  'get_subject_scope',
  'get_role_function',
  'get_challenges',
  'get_role_context',
  'review_role_context',
  'get_evaluation_scope',
]);

/**
 * Works out whether a loaded conversation is a talent audit.
 *
 * Needed because a talent audit is keyed by assessment_id exactly like flow_2, so
 * the `?id=` URL param can't tell them apart on reload. The history endpoint is
 * no help either — it reports `flow_type: "hiring"` for audits too — so the
 * reliable signal is the audit-only states in the transcript. The `flow_type` and
 * numeric `flow` checks are kept first in case the backend starts labelling these
 * properly later.
 */
export const isTalentAuditConversation = (data: any): boolean => {
  if (!data) return false;
  if (data.flow_type === TALENT_AUDIT_FLOW_TYPE) return true;
  if (data.flow === TALENT_AUDIT_FLOW_NUMBER) return true;
  if (TALENT_AUDIT_STATES.has(data.current_state)) return true;
  return (data.conversation_history || []).some((entry: any) => {
    if (TALENT_AUDIT_STATES.has(entry?.state)) return true;

    // Every Flow 3 branch begins with one of these values at get_audit_type.
    // Detect the value even if a backend version renames that state; these
    // values are unique to Flow 3 and are more stable than its internal states.
    return (
      entry?.role === 'user' &&
      typeof entry?.content === 'string' &&
      TALENT_AUDIT_TYPE_VALUE_SET.has(entry.content)
    );
  });
};

/**
 * Adds `flow_type` to an outgoing hiring-assistant payload when the recruiter is
 * in the talent audit flow. Handles both the JSON and FormData request shapes so
 * callers don't have to care which one they're building.
 */
export const withFlowType = <T>(payload: T): T => {
  if (!isTalentAudit()) return payload;

  if (payload instanceof FormData) {
    payload.set('flow_type', TALENT_AUDIT_FLOW_TYPE);
    return payload;
  }

  return { ...(payload as object), flow_type: TALENT_AUDIT_FLOW_TYPE } as T;
};
